import { MoreHoriz } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WooCommerceOnboardingPlatformLogoBox, {
  fadedPrimaryBg,
} from "features/onboarding/woocommerce/WooCommerceOnboardingPlatformLogoBox";
import {
  BingAdsTrackingTemplate,
  GoogleAdsTrackingTemplate,
  MetaAdsTrackingTemplate,
} from "features/utms";
import {
  I_ADID,
  I_CAMPAIGN,
  I_CAMPAIGNID,
  I_CONTENTID,
  I_MEDIUM,
  I_SOURCE,
  I_TERM,
} from "features/utms/constants";
import { useState } from "react";

const TRACKING_PARAMS = [
  {
    param: I_SOURCE,
    purpose: "Traffic source (e.g. platform, TikTok, etc.)",
  },
  { param: I_MEDIUM, purpose: "Medium (e.g. cpc, paid social)" },
  { param: I_CAMPAIGN, purpose: "Campaign name" },
  { param: I_CAMPAIGNID, purpose: "Campaign ID" },
  { param: I_CONTENTID, purpose: "Ad group, ad set, or content ID" },
  { param: I_TERM, purpose: "Keyword, ad name, or placement" },
  { param: I_ADID, purpose: "Individual ad ID (when available)" },
];

function WooCommerceOnboardingOtherPlatforms() {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: 1,
        borderColor: "grey.200",
        bgcolor: (theme) => fadedPrimaryBg(theme, 0.08),
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.5,
        }}
      >
        <WooCommerceOnboardingPlatformLogoBox sx={{ color: "primary.main" }}>
          <MoreHoriz fontSize="small" />
        </WooCommerceOnboardingPlatformLogoBox>
        <Typography sx={{ flex: 1, fontWeight: 500 }}>
          Other platforms
        </Typography>
        <Button
          variant="text"
          color="primary"
          size="small"
          sx={{ minWidth: "auto", fontWeight: 600, textTransform: "none" }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Hide" : "View"}
        </Button>
      </Box>
      <Collapse in={expanded}>
        <Stack gap={2} sx={{ px: 2, pb: 2.5, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            For platforms not listed above, add Incendium tracking parameters to
            your destination URLs or the tracking fields your ad platform
            provides. Each platform uses different placeholders — map our
            parameters to the equivalents your tool supports (campaign ID, ad
            set ID, and so on).
          </Typography>
          <Alert severity="info" sx={{ py: 0.5 }}>
            We cannot pull spend data for these sources yet. Attribution from
            clicks and visits still works when parameters are set correctly, and
            we are adding more platform integrations over time.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Parameter</TableCell>
                <TableCell>Purpose</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TRACKING_PARAMS.map((row) => (
                <TableRow key={row.param}>
                  <TableCell>
                    <Typography
                      component="code"
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {row.param}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.purpose}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="subtitle2" fontWeight={600}>
            Examples by platform
          </Typography>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "grey.200",
              borderRadius: 1.5,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={500}>
                Google Ads
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <GoogleAdsTrackingTemplate />
            </AccordionDetails>
          </Accordion>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "grey.200",
              borderRadius: 1.5,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={500}>
                Meta Ads
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MetaAdsTrackingTemplate />
            </AccordionDetails>
          </Accordion>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "grey.200",
              borderRadius: 1.5,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={500}>
                Bing Ads
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <BingAdsTrackingTemplate />
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Collapse>
    </Box>
  );
}

export default WooCommerceOnboardingOtherPlatforms;
